/**
 * Conversation-slice lifecycle resets — they live next to the slice state they drive.
 *
 * Two effects, both early-returning when disabled:
 *   A. user-change / AI-just-enabled reset (deps [isEnabled, userGuid]) — clears persisted storage
 *      then collapses the conversation to defaults. Three guarding refs implement the required
 *      stale-ref semantics: prevUserGuidRef is only advanced while enabled (kept stale while
 *      disabled so a guid change during a disabled window is detectable on re-enable),
 *      isFirstAIEnabledRunRef skips the first enabled run (preserves restored localStorage on
 *      load/refresh), prevIsAIEnabledRef tracks the enable transition. These refs MUST survive
 *      isEnabled flips, so this hook is invoked UNCONDITIONALLY in the provider body.
 *   B. new-day reset wired to `visibilitychange` — on a new calendar day the conversation resets,
 *      storage clears, and the active day is restamped (the new-day rule is shared with the
 *      composition root via constants/storageKeys).
 *
 * `isEnabled` mirrors the provider's `areActionsEnabled` (AIChatProvider passes isAIEnabled), and
 * `actions` is the REAL (ungated) action object — both effects early-return when disabled, so the
 * ungated setters are never invoked in the disabled state.
 *
 * It must NEVER import from contexts/AIChatProvider (module cycle; that module is also stubbed
 * in some suites). useUser / clearChatbotStorage use the absolute specifiers the composition
 * suites mock.
 */
import { useUser } from 'core/lite/hooks/useUser';
import { clearChatbotStorage } from 'core/utils/utils';
import { useEffect, useRef } from 'react';
import { STREAM_STATUS } from '../../constants/status';
import { isNewDaySinceLastActive, stampActiveDay } from '../../constants/storageKeys';
import { isPracticeLaunchTab } from '../../utils/practiceLaunch';
import type { AIChatConversationActions } from './AIChatConversationProvider';

type Params = {
  isEnabled: boolean;
  actions: AIChatConversationActions;
};

export function useConversationLifecycleResets({ actions, isEnabled }: Params): void {
  const { setCurrentConversationId, setStreamStatus, clearMessageQueue, clearConversationData } = actions;

  // Monitor user changes to reset conversation state on login/SSO/logout
  const { user } = useUser();
  const userGuid = user?.guid;
  const prevUserGuidRef = useRef(userGuid);
  const prevIsAIEnabledRef = useRef(isEnabled);
  const isFirstAIEnabledRunRef = useRef(true);

  // Clear conversation state when user changes OR when isEnabled transitions false→true
  useEffect(() => {
    const previousGuid = prevUserGuidRef.current;
    const wasAIEnabled = prevIsAIEnabledRef.current;

    // Always track isEnabled transitions
    prevIsAIEnabledRef.current = isEnabled;

    // When AI is disabled, don't update user ref and don't reset.
    // This intentionally keeps prevUserGuidRef stale so that when AI re-enables,
    // we can detect whether the user actually changed.
    if (!isEnabled) return;

    // Update user ref only when AI is enabled
    prevUserGuidRef.current = userGuid;

    // Skip the first effect run where isEnabled=true (initial app load / refresh)
    // to preserve restored conversation state from localStorage.
    if (isFirstAIEnabledRunRef.current) {
      isFirstAIEnabledRunRef.current = false;
      return;
    }

    // Reset when:
    // 1. User guid changed (different user logged in)
    // 2. isEnabled transitioned false → true (same-user logout/login cycle)
    const userChanged = previousGuid !== userGuid;
    const aiJustEnabled = !wasAIEnabled && isEnabled;

    if (userChanged || aiJustEnabled) {
      // A tab launched from a study chat card never writes the study tabs' storage (TANAI-2622).
      if (!isPracticeLaunchTab()) clearChatbotStorage();
      setCurrentConversationId(null);
      setStreamStatus(STREAM_STATUS.IDLE);
      clearMessageQueue();
      clearConversationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, userGuid]);

  // Reset conversation when a new day is detected on tab focus (handles long-running sessions).
  // Skipped in a tab launched from a study chat card: it would wipe the conversation the student
  // goes back to in the study tab (TANAI-2622).
  useEffect(() => {
    if (!isEnabled || isPracticeLaunchTab()) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isNewDaySinceLastActive()) {
        setCurrentConversationId(null);
        setStreamStatus(STREAM_STATUS.IDLE);
        clearMessageQueue();
        clearConversationData();
        clearChatbotStorage();
        stampActiveDay();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled]);
}
